/**
 * useSubscription Hook
 * 
 * Custom hook for accessing subscription context and state.
 * Provides easy access to subscription data and actions.
 */

import { useContext } from 'react';
import { SubscriptionContext } from '../context/SubscriptionContext';

/**
 * useSubscription Hook
 * 
 * @returns {Object} Subscription context value
 * @throws {Error} If used outside of SubscriptionProvider
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

/**
 * useSubscriptionStatus Hook
 * 
 * Returns only subscription status and related computed properties
 * 
 * @returns {Object} Subscription status data
 */
export const useSubscriptionStatus = () => {
  const { status, computed, loading, error } = useSubscription();
  
  return {
    status,
    isActive: computed.isActive,
    isExpired: computed.isExpired,
    isInactive: computed.isInactive,
    needsRenewal: computed.needsRenewal,
    isInGracePeriod: computed.isInGracePeriod,
    statusLabel: computed.statusLabel,
    loading: loading.status,
    error
  };
};

/**
 * useSubscriptionFeatures Hook
 * 
 * Returns subscription features and feature checking utilities
 * 
 * @returns {Object} Subscription features data
 */
export const useSubscriptionFeatures = () => {
  const { features, type, computed } = useSubscription();
  
  return {
    features,
    type,
    typeLabel: computed.typeLabel,
    hasFeature: computed.hasFeature,
    hasSurebetAccess: computed.hasFeature('surebet_access'),
    hasCalculatorAccess: computed.hasFeature('calculator_access'),
    hasFilterAccess: computed.hasFeature('filter_access'),
    hasRealtimeUpdates: computed.hasFeature('realtime_updates'),
    hasHistoricalData: computed.hasFeature('historical_data'),
    hasAdvancedAnalytics: computed.hasFeature('advanced_analytics'),
    hasPrioritySupport: computed.hasFeature('priority_support')
  };
};

/**
 * useSubscriptionActions Hook
 * 
 * Returns only subscription actions
 * 
 * @returns {Object} Subscription actions
 */
export const useSubscriptionActions = () => {
  const { actions } = useSubscription();
  
  return actions;
};

/**
 * useSubscriptionLoading Hook
 * 
 * Returns loading states for different subscription operations
 * 
 * @returns {Object} Loading states
 */
export const useSubscriptionLoading = () => {
  const { loading } = useSubscription();
  
  return {
    ...loading,
    anyLoading: Object.values(loading).some(Boolean)
  };
};

/**
 * useSubscriptionError Hook
 * 
 * Returns error state and error management actions
 * 
 * @returns {Object} Error state and actions
 */
export const useSubscriptionError = () => {
  const { error, actions } = useSubscription();
  
  return {
    error,
    hasError: !!error,
    clearError: actions.clearError,
    setError: actions.setError
  };
};

export default useSubscription;
